import React from 'react';
import { IOption } from 'components/Select/Select';
import {
  CountOfReportsInfo,
  ReportLists,
  SavedReportsWrapper,
  SavedReportsContainer,
} from './styles';
import messages from '../Messages';
import SavedReportItem from './SavedReportItem';
import CheckPointModal from 'components/CheckPointModal';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { isANumber } from '../../../utils/number';
import Collapsible from '../../Collapsible/Collapsible';
import {
  ChartTypes,
  IInstanceReportField,
  IQuestionReportField,
  IReportView,
  ISeries,
  ISurveyItem,
  ISurveyView,
  Metric,
  PeriodType,
  SeriesType,
} from 'store/survey/surveyTypes';
import { IOpenItem } from 'store/entity/IOpenItem';
import cloneDeep from 'lodash/cloneDeep';

interface IComponentProps {
  canUpdate: boolean;
  isActive: boolean;
  onPrepareReport: (reportId?: number, reportObj?: IReportView) => void;
  onCopyReport: (reportId: number) => void;
  onDeleteReport: (reportId: number) => void;
  reportItem?: IOpenItem;
  reportsTags: IOption[];
  saveReport: (report: IReportView) => void;
  selectedReport: number | null;
  survey: ISurveyView;
  surveyItem: ISurveyItem;
  oldVersion?: boolean;
  showExtraReports?: boolean;
  setAutoReportList: (reports: IReportView[]) => void;
  providerId: number;
}

interface IComponentStates {
  reports?: { [key: number]: IReportView };
  splitReports?: { [key: number]: IReportView };
  extraReports?: { [key: number]: IReportView };
  checkPointsModal: boolean;
  usePureValues: boolean;
}

function periodLengthFor(periodType: PeriodType | undefined) {
  switch (periodType) {
    case 'none':
      return 12;
    case 'day':
      return 60;
    case 'week':
      return 10;
    case 'month':
      return 12;
    case 'season':
      return 4;
    case 'twoSeason':
      return 2;
    case 'year':
      return 3;
    case undefined:
      return 12;
  }
}

function getValueMetric(metric: Metric): Metric {
  switch (metric) {
    case 'percentageOfCount':
      return 'count';
    case 'percentageOfAvg':
      return 'avg';
    case 'percentageOfTotalAvg':
      return 'totalAvg';
    default:
      return metric;
  }
}

class AutoReportList extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state: IComponentStates = {
    reports: undefined,
    checkPointsModal: false,
    usePureValues: false,
  };

  private createReports() {
    const survey = this.props.survey as ISurveyView;
    let reports = [
      ...this.totalReports(survey),
      // ...this.totalReportsInSpecificTime(survey),
      ...this.perChoiceReports(survey, true),
      ...this.questionSplitReports(survey, true),
    ];
    if (this.props.surveyItem.instanceType === 'User') {
      reports = [...reports, ...this.userSplit(survey)];
    }
    let extraReports = [
      ...(this.totalCountReports(
        survey,
        reports.length === 0
      ) as IReportView[]),
    ];
    if (reports.length === 0) {
      reports = extraReports;
      extraReports = [];
    }
    this.setState(
      {
        reports: reports.reduce((current, item) => {
          current[item.id] = item;
          return current;
        }, {}),
      },
      () => {
        this.state.reports && this.props.setAutoReportList(reports);
      }
    );
    this.setState({
      extraReports: extraReports.reduce((current, item) => {
        current[item.id] = item;
        return current;
      }, {}),
    });
  }

  private totalReports(survey: ISurveyView) {
    const perQuestionSeries = this.getPerQuestionSeries(
      survey,
      true,
      true,
      false,
      false
    );
    const splitQuestionSeries = this.getSplitQuestionSeries(
      survey,
      true,
      true
    ) as ISeries[];
    const tagSeries = this.tagSeries(survey, 'table', true) as ISeries[];
    const timeScale = this.getTimeScale(survey);
    const hasValue = this.getHasValue(survey);
    const usePureValues =
      Boolean(
        Object.values(survey.questionMap || {}).find(
          (it) =>
            !it.data.deleted &&
            it.data.type === 'indicator' &&
            it.data.weight !== 0
        )
      ) || this.state.usePureValues;
    const all = hasValue
      ? [
          {
            type: 'all',
            chartType: 'bar',
            metric: usePureValues ? 'totalAvg' : 'percentageOfTotalAvg',
          } as ISeries,
        ]
      : [];

    const reportBase = {
      time: {
        timeScale: timeScale,
        splitOnTime: true,
        timeLength: periodLengthFor(timeScale),
        totalTime: true,
        similarPastYearPeriodLength: undefined,
      },
      order: -10020,
      series: [],
      surveyId: survey.id,
      id: -10020,
      name: messages.reportInTime(usePureValues, survey, timeScale),
      tags: [],
      fields: [],
      dashboardLinks: [],
      dashboardHeight: undefined,
      dashboardWidth: undefined,
      ownerId: undefined,
    };
    const result = [
      ...all.map((it) => {
        return {
          ...reportBase,
          series: [it],
          name: messages.reportInTime(
            usePureValues,
            survey,
            reportBase.time.timeScale
          ),
          id: -11030,
          order: -11030,
        };
      }),
    ];
    if (tagSeries.length > 0 || splitQuestionSeries.length > 0)
      result.push({
        ...reportBase,
        series: [...tagSeries, ...splitQuestionSeries],
        name: messages.reportInTimePerAxis(
          usePureValues,
          survey,
          reportBase.time.timeScale
        ),
        id: -11025,
        order: -11025,
      });
    if (
      !survey.fields.some(
        (it) =>
          it.name === 'conf_excludeDetailedGridReports' && it.value === 'true'
      ) &&
      perQuestionSeries.length > 0
    )
      perQuestionSeries.forEach((it, index) =>
        result.push({
          ...reportBase,
          series: [it],
          name: messages.reportInTimePerItem(
            usePureValues,
            reportBase.time.timeScale,
            survey.questionMap![Number(it.questionId!)].data.title
          ),
          id: -125 + index,
          order: -125 + index,
        })
      );
    return result;
  }

  private getHasValue(survey: ISurveyView) {
    return Boolean(
      Object.values(survey.questionMap || {}).find(
        (it) =>
          !it.data.deleted &&
          !!it.data.weight &&
          (((it.data.type === 'multipleChoice' || it.data.type === 'grid') &&
            it.data.choiceList.find((it) => isANumber(it.score))) ||
            it.data.type === 'indicator' ||
            (it.data.type === 'multipleAnswer' &&
              it.data.questionFields.find((it) => it.withValue)))
      )
    );
  }

  private userSplit(survey: ISurveyView) {
    if (
      !Object.values(survey.questionMap || {}).find(
        ({ data }) =>
          (!data.deleted && data.type === 'indicator') ||
          ((data.type === 'multipleChoice' || data.type === 'grid') &&
            data.choiceList.find((it) => it.score !== null))
      )
    )
      return [];

    const timeScale = this.getTimeScale(survey);
    return [
      {
        time: {
          timeScale: timeScale,
          splitOnTime: true,
          timeLength: periodLengthFor(timeScale),
          totalTime: true,
          similarPastYearPeriodLength: undefined,
        },
        order: -10019.5,
        series: [
          ...[
            {
              type: 'all',
              chartType: 'table',
              metric:
                Object.values(survey.questionMap || {}).find(
                  (it) =>
                    !it.data.deleted &&
                    it.data.type === 'indicator' &&
                    it.data.weight !== 0
                ) || this.state.usePureValues
                  ? 'totalAvg'
                  : 'percentageOfTotalAvg',
            },
          ],
        ],
        surveyId: survey.id,
        id: -10019.5,
        name: messages.reportInTimePerUser(
          this.state.usePureValues,
          survey,
          timeScale
        ),
        tags: [],
        fields: [
          {
            type: 'instance',
            split: true,
            instanceType: 'User',
            includeTotal: true,
            values: [],
          } as IInstanceReportField,
        ],
        dashboardLinks: [],
        dashboardHeight: undefined,
        dashboardWidth: undefined,
        ownerId: undefined,
      } as IReportView,
    ];
  }

  private perChoiceReports(
    survey: ISurveyView,
    onlyWithScore: boolean = false
  ) {
    const perQuestionSeries = this.getPerChoiceSeries(survey, onlyWithScore);
    const timeScale = this.getTimeScale(survey);

    return perQuestionSeries.length
      ? [
          {
            time: {
              timeScale: timeScale,
              splitOnTime: true,
              timeLength: periodLengthFor(timeScale),
              totalTime: false,
              similarPastYearPeriodLength: undefined,
            },
            order: -1010,
            series: [...perQuestionSeries],
            surveyId: survey.id,
            id: -1010,
            name: this.state.usePureValues
              ? messages.choiceSelectCount(timeScale)
              : messages.choiceSelectPercent(timeScale),
            tags: [],
            fields: [],
            dashboardLinks: [],
            dashboardHeight: undefined,
            dashboardWidth: undefined,
            ownerId: undefined,
          },
        ]
      : [];
  }

  private tagSeries(
    survey: ISurveyView,
    chartType: ChartTypes = 'bar',
    allowSplit: boolean = true
  ) {
    const tags = this.getQuestionTags(survey);
    const sorted = Object.values(tags).sort((a, b) => a.order - b.order);
    return allowSplit && sorted.length > 0
      ? [
          {
            type: 'tag',
            chartType: 'table',
            metric:
              sorted.find((it) => it.hasIndicator) || this.state.usePureValues
                ? 'totalAvg'
                : 'percentageOfTotalAvg',
            tagIds: sorted.map((it) => {
              return it.id;
            }),
            split: true,
          },
        ]
      : sorted.map((it) => {
          return {
            type: 'tag',
            chartType: chartType,
            metric:
              it.hasIndicator || this.state.usePureValues
                ? 'totalAvg'
                : 'percentageOfTotalAvg',
            tagIds: [it.id],
          } as ISeries;
        });
  }

  private getPerQuestionSeries(
    survey: ISurveyView,
    allowSplit: boolean = true,
    includeGrid: boolean = false,
    includeIndicator: boolean = false,
    includeMultiChoice: boolean = false
  ) {
    const ret = Object.values(survey.questionMap || {})
      .sort((a, b) => a.data.order - b.data.order)
      .filter((it) => !it.data.deleted)
      .reduce((current: ISeries[], item) => {
        if (
          (includeMultiChoice &&
            item.data.type === 'multipleChoice' &&
            item.data.choiceList.find((it) => isANumber(it.score))) ||
          (includeIndicator && item.data.type === 'indicator') ||
          (includeGrid && item.data.type === 'grid')
        ) {
          current.push({
            type: 'question',
            chartType:
              item.data.type === 'grid'
                ? 'table'
                : allowSplit
                  ? 'bar'
                  : 'table',
            metric:
              item.data.type === 'indicator'
                ? !item.data.denominator
                  ? 'sum'
                  : 'totalAvg'
                : !this.state.usePureValues &&
                    !(
                      item.data.type === 'multipleChoice' &&
                      !item.data.multiSelect
                    )
                  ? 'percentageOfTotalAvg'
                  : 'totalAvg',
            subQuestionId:
              item.data.type === 'grid' && allowSplit ? '' : undefined,
            questionId: item.data.id.toString(),
            includeTotal: true,
          });
        }
        if (
          includeIndicator &&
          item.data.type === 'indicator' &&
          item.data.denominator
        ) {
          current.push({
            type: 'question',
            chartType: 'table',
            metric: 'totalAvg',
            subQuestionId: 'numerator',
            questionId: item.data.id.toString(),
          });
          current.push({
            type: 'question',
            chartType: 'table',
            metric: 'totalAvg',
            subQuestionId: 'denominator',
            questionId: item.data.id.toString(),
          });
        }

        return current;
      }, [] as ISeries[]);
    return ret;
  }
  private getSplitQuestionSeries(
    survey: ISurveyView,
    noSplit: boolean = false,
    onlyWithValue: boolean = false
  ) {
    const ret = Object.values(survey.questionMap || {})
      .sort((a, b) => a.data.order - b.data.order)
      .filter((it) => !it.data.deleted)
      .reduce(
        (current: ISeries, item) => {
          if (
            (item.data.type === 'grid' ||
              item.data.type === 'multipleChoice') &&
            (!onlyWithValue ||
              item.data.choiceList?.find((it) => it.score !== null))
          ) {
            if (current.questionId) current.questionId += ',';
            current.questionId! += item.data.id;
          }
          return current;
        },
        {
          type: 'question',
          chartType: noSplit ? 'table' : 'bar',
          metric: this.state.usePureValues
            ? 'totalAvg'
            : 'percentageOfTotalAvg',
          includeTotal: true,
          questionId: '',
        } as ISeries
      );
    if (ret.questionId?.includes(',')) return [ret];
    return [];
  }

  private getPerChoiceSeries(
    survey: ISurveyView,
    onlyWithScore: boolean = false,
    includeGrid: boolean = false
  ) {
    const ret = Object.values(survey.questionMap || {})
      .sort((a, b) => a.data.order - b.data.order)
      .filter((it) => !it.data.deleted)
      .reduce((current: ISeries[], item) => {
        if (
          item.data.type === 'multipleChoice' ||
          (item.data.type === 'grid' && includeGrid)
        ) {
          if (onlyWithScore && !item.data.choiceList?.find((it) => it.score))
            return current;
          current.push({
            type: 'question',
            chartType: 'table',
            metric: this.state.usePureValues ? 'count' : 'percentageOfCount',
            subQuestionId: undefined,
            questionId: item.data.id.toString(),
            splitOnValues: true,
          });
        }
        return current;
      }, [] as ISeries[]);
    return ret;
  }

  private getQuestionTags(survey: ISurveyView) {
    const tags: {
      [key: number]: {
        count: number;
        order: number;
        id: number;
        hasIndicator: boolean;
      };
    } = {};
    Object.values(survey.questionMap || {})
      .sort((a, b) => a.data.order - b.data.order)
      .filter((it) => !it.data.deleted)
      .forEach((item) => {
        if (
          (item.data.type === 'multipleChoice' &&
            item.data.choiceList.find((it) => isANumber(it.score))) ||
          item.data.type === 'indicator' ||
          item.data.type === 'grid'
        )
          item.data.tags?.forEach((tag) => {
            if (!tags[tag.id]) {
              tags[tag.id] = {
                count: 1,
                order: Object.keys(tags).length,
                id: tag.id,
                hasIndicator: item.data.type === 'indicator',
              };
            } else {
              tags[tag.id].count++;
              if (item.data.type === 'indicator')
                tags[tag.id].hasIndicator = true;
            }
          });
      });
    return tags;
  }

  private questionSplitReports(
    survey: ISurveyView,
    showInPeriodSeparateReport: boolean
  ) {
    // const perQuestionSeries = this.getPerQuestionSeries(survey, true);
    const splitQuestionSeries = this.getSplitQuestionSeries(survey, true, true);
    const tagSeries = this.tagSeries(survey, 'bar', false);
    const splitQuestions = Object.values(survey.questionMap || {})
      .sort((a, b) => a.data.order - b.data.order)
      .filter((it) => !it.data.deleted)
      .filter((item) => {
        return (
          item.data.type === 'multipleChoice' &&
          (item.data.distinctive || item.data.showInTitle)
        );
      }, [] as ISeries[]);

    return splitQuestions.reduce((current, q) => {
      const timeScale = this.getTimeScale(survey);
      let index = current.length;

      const hasValue = this.getHasValue(survey);
      const allSeries = hasValue
        ? [
            {
              type: 'all' as SeriesType,
              chartType: 'bar' as ChartTypes,
              metric: (this.state.usePureValues
                ? 'totalAvg'
                : 'percentageOfTotalAvg') as Metric,
            },
          ]
        : [];
      const series = [...allSeries, ...splitQuestionSeries, ...tagSeries];
      if (
        splitQuestionSeries.length > 0 ||
        (tagSeries.length > 0 && showInPeriodSeparateReport)
      )
        current.push({
          time: {
            timeScale: timeScale,
            splitOnTime: false,
            timeLength: 1,
            totalTime: true,
            similarPastYearPeriodLength: undefined,
          },
          order: -1000 + index,
          // @ts-expect-error Metric is string!
          series: series,
          surveyId: survey.id,
          id: -1000 + index,
          name: messages.splitOn(
            q.data.title,
            this.state.usePureValues,
            survey.name,
            timeScale
          ),
          tags: [],
          fields: [
            {
              type: 'question',
              questionId: q.data.id,
              split: true,
              includeTotal: true,
              subQuestionId: null,
              choiceIds: [],
            } as IQuestionReportField,
          ],
          dashboardLinks: [],
          dashboardHeight: undefined,
          dashboardWidth: undefined,
          ownerId: undefined,
        });
      if (q.data.distinctive || q.data.showInTitle) {
        index = current.length;
        const seriesForTime = [
          ...allSeries.map((it) => {
            return {
              ...it,
              chartType: 'table',
            } as ISeries;
          }),
        ];
        if (series.length)
          current.push({
            time: {
              timeScale: timeScale,
              splitOnTime: true,
              timeLength: periodLengthFor(timeScale),
              totalTime: true,
              similarPastYearPeriodLength: undefined,
            },
            order: -1000 + index,
            series: seriesForTime,
            surveyId: survey.id,
            id: -1000 + index,
            name: messages.splitOnAndTime(
              q.data.title,
              this.state.usePureValues,
              survey,
              timeScale
            ),
            tags: [],
            fields: [
              {
                type: 'question',
                questionId: q.data.id,
                split: true,
                includeTotal: true,
                subQuestionId: null,
                choiceIds: [],
              } as IQuestionReportField,
            ],
            dashboardLinks: [],
            dashboardHeight: undefined,
            dashboardWidth: undefined,
            ownerId: undefined,
          });
      }
      return current;
    }, [] as IReportView[]);
  }

  private totalCountReports(survey: ISurveyView, split: boolean = false) {
    const perQuestionSeries = Object.values(survey.questionMap || {})
      .sort((a, b) => a.data.order - b.data.order)
      .filter((it) => !it.data.deleted)
      .reduce((current: ISeries[], item) => {
        if (
          item.data.type === 'multipleChoice' &&
          (item.data.distinctive ||
            item.data.showInTitle ||
            item.data.useTags ||
            !item.data.choiceList.find((it) => isANumber(it.score)))
        )
          current.push({
            type: 'question',
            chartType: 'stack',
            metric: this.state.usePureValues ? 'count' : 'percentageOfCount',
            splitOnValues: true,
            questionId: item.data.id.toString(),
            name: item.data.title,
          });
        return current;
      }, [] as ISeries[]);
    const timeScale = this.getTimeScale(survey);
    const mainCountSeries = { type: 'all', chartType: 'bar', metric: 'count' };
    const series = [mainCountSeries, ...perQuestionSeries];
    const reportTemplate = {
      time: {
        timeScale: timeScale,
        splitOnTime: true,
        timeLength: periodLengthFor(timeScale),
        totalTime: false,
        similarPastYearPeriodLength: undefined,
      },
      order: -1250,
      series: series,
      surveyId: survey.id,
      id: -1250,
      name: messages.fillCountName(this.state.usePureValues, timeScale, survey),
      tags: [],
      fields: [],
      dashboardLinks: [],
      dashboardHeight: undefined,
      dashboardWidth: undefined,
      ownerId: undefined,
    };
    if (!split) return [reportTemplate];
    return [
      {
        ...reportTemplate,
        series: [{ type: 'all', chartType: 'bar', metric: 'count' }],
        name: `${messages.fillCount} ${messages.reportNameExtractor(survey)}`,
      },
      ...perQuestionSeries.map((it, index) => {
        return {
          ...reportTemplate,
          series: [it],
          name: messages.fillCountName(
            this.state.usePureValues,
            timeScale,
            survey,
            it.name
          ),
          id: reportTemplate.id + index + 1,
          order: reportTemplate.id + index + 1,
        };
      }),
    ];
  }

  private getTimeScale(survey: ISurveyView) {
    switch (survey.periodType) {
      case 'none':
        return 'month';
      case 'day':
        return 'month';
    }
    return survey.periodType;
  }

  componentDidMount() {
    const survey = this.props.survey;
    if (
      Object.values(survey.questionMap || {}).find(
        (it) =>
          it.data.type === 'indicator' &&
          !it.data.deleted &&
          it.data.weight !== 0
      )
    ) {
      this.setState({ usePureValues: true }, () => {
        this.createReports();
      });
    } else this.createReports();
  }

  handleShowCheckPointModal = (open: boolean) => {
    this.setState({
      checkPointsModal: open,
    });
  };

  setUsePureValues = (usePureValues: boolean) => {
    this.setState({ usePureValues: usePureValues }, () => {
      this.createReports();
    });
  };

  onChooseAReport =
    (report: IReportView) =>
    (reportId: number, type: 'analyse' | 'edit' | 'pureValue') => {
      if (type === 'analyse') this.props.onPrepareReport(reportId, report);
      else if (type === 'pureValue') {
        const valueReport = cloneDeep(report);
        valueReport.series = valueReport.series.map((it: ISeries) => {
          return {
            ...it,
            metric: getValueMetric(it.metric),
          };
        });
        let targetMetricName = messages.amount;
        if (valueReport.series[0].metric === 'count') targetMetricName = '';
        valueReport.name = valueReport.name.replace(
          messages.percentage,
          targetMetricName
        );
        this.props.onPrepareReport(reportId, valueReport);
      }
    };
  render() {
    const {
      isActive,
      selectedReport,
      canUpdate,
      reportItem,
      survey,
      showExtraReports,
    } = this.props;
    const { reports, splitReports, extraReports } = this.state;

    const reportList = reports
      ? Object.values(reports).reduce((current, report) => {
          current.push(report);
          return current!;
        }, [] as Array<IReportView>)
      : [];
    const splitReportList = splitReports
      ? Object.values(splitReports).reduce((current, report) => {
          current!.push(report);
          return current!;
        }, [] as Array<IReportView>)
      : [];
    const exptraReportList = extraReports
      ? Object.values(extraReports).reduce((current, report) => {
          current!.push(report);
          return current!;
        }, [] as Array<IReportView>)
      : [];

    return (
      <>
        <SavedReportsContainer isActive={isActive}>
          <SavedReportsWrapper>
            {reportList ? (
              // @ts-expect-error JSX error
              <DragDropContext
                onDragEnd={() => {
                  //   Do Nothing
                }}
              >
                <Droppable droppableId={'auto-report-list'}>
                  {(provide) => (
                    <ReportLists ref={provide.innerRef}>
                      {reportList
                        .sort((a, b) => {
                          if (a.order && b.order) {
                            return a.order - b.order;
                          }
                          return 0;
                        })
                        .map((report: IReportView, index?: number) => {
                          return (
                            <SavedReportItem
                              canUpdate={false}
                              index={index}
                              isReportItem={!!reportItem}
                              key={report.id}
                              onChooseAReport={this.onChooseAReport(report)}
                              onCopyReport={undefined}
                              onDeleteReport={undefined}
                              report={report}
                              selectedReport={selectedReport}
                              oldVersion={false}
                            />
                          );
                        })}
                    </ReportLists>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <CountOfReportsInfo>
                <span>{messages.noAutoReportExists}</span>
              </CountOfReportsInfo>
            )}
            {this.state.checkPointsModal && (
              <CheckPointModal
                active={this.state.checkPointsModal}
                canUpdate={canUpdate}
                onClose={() => this.handleShowCheckPointModal(false)}
                survey={survey}
                surveyReport={reportItem}
                providerId={this.props.providerId}
              />
            )}
          </SavedReportsWrapper>
        </SavedReportsContainer>
        {showExtraReports &&
          [
            { list: splitReportList, name: messages.splitReports },
            { list: exptraReportList, name: messages.extraReports },
          ].map((it, idx: number) =>
            it?.list?.length ? (
              <Collapsible
                key={idx}
                iconName="grid"
                customBodyStyle={{ padding: 0 }}
                header={
                  <div>
                    <span>{it.name}</span>{' '}
                  </div>
                }
              >
                <SavedReportsContainer isActive={isActive}>
                  <SavedReportsWrapper>
                    {/* @ts-expect-error JSX error*/}
                    <DragDropContext
                      onDragEnd={() => {
                        /*do nothing*/
                      }}
                    >
                      <Droppable droppableId={'auto-report-list'}>
                        {(provid) => (
                          <ReportLists ref={provid.innerRef}>
                            {it.list
                              .sort((a, b) => {
                                if (a.order && b.order) {
                                  return a.order - b.order;
                                }
                                return 0;
                              })
                              .map((report: IReportView, index?: number) => {
                                return (
                                  <SavedReportItem
                                    canUpdate={false}
                                    index={index}
                                    isReportItem={!!reportItem}
                                    key={report.id}
                                    onChooseAReport={this.onChooseAReport(
                                      report
                                    )}
                                    onCopyReport={undefined}
                                    onDeleteReport={undefined}
                                    report={report}
                                    selectedReport={selectedReport}
                                    oldVersion={false}
                                  />
                                );
                              })}
                          </ReportLists>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </SavedReportsWrapper>
                </SavedReportsContainer>
              </Collapsible>
            ) : (
              <span key={idx} />
            )
          )}
      </>
    );
  }
}
export default AutoReportList;
